<template>
  <AppNavbar />
  <div class="container mt-4">
    <AppHeader
      title="Ingreso Varios"
      subtitle="Registrar ingresos diversos no relacionados a ventas"
    >
      <template #buttons>
        <router-link class="btn btn-outline-secondary" :to="{ name: 'Caja' }">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </router-link>
      </template>
    </AppHeader>

    <div v-if="feedback.message" class="alert mt-4" :class="feedback.variant">
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
        <div>
          <div class="fw-semibold">{{ feedback.message }}</div>
          <small v-if="feedback.detail" class="d-block text-muted mt-1">{{ feedback.detail }}</small>
        </div>
        <button
          v-if="feedback.canRetry && feedback.movementId > 0"
          class="btn btn-outline-secondary btn-sm"
          :disabled="isRetryingPrint"
          @click="retryReceiptPrint"
        >
          <span
            v-if="isRetryingPrint"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Reintentar impresión
        </button>
      </div>
    </div>

    <div class="row mt-4">
      <!-- Columna izquierda: Información del Ingreso -->
      <div class="col-12 col-md-7">
        <div class="card p-4 mb-4 h-100">
          <h5><i class="bi bi-graph-up me-2"></i>Información del Ingreso</h5>
          <p class="text-muted">Datos del ingreso varios</p>
          <div class="row g-3 mt-3">
            <div class="col-md-4">
              <select v-model="form.category" class="form-select">
                <option disabled value="">Seleccionar categoría</option>
                <option v-for="c in categories" :key="c" :value="c">
                  {{ c }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <!-- `emit-zero-when-empty`: `form.amount` alimenta el prop
                   `total` de PaymentForms, que es Number y requerido. Un campo
                   vaciado emitiendo `null` rompería el cálculo de vuelto. -->
              <MoneyInput
                v-model="form.amount"
                placeholder="0"
                emit-zero-when-empty
              />
            </div>
            <div class="col-12">
              <input
                type="text"
                v-model="form.reference"
                class="form-control"
                placeholder="Número de documento, referencia, etc."
              />
            </div>
            <div class="col-12">
              <textarea
                v-model="form.description"
                class="form-control"
                rows="6"
                placeholder="Descripción detallada del ingreso..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>
            <!-- Columna derecha: Formas de Pago -->
      <div class="col-12 col-md-5">
        <div class="card p-3 mb-4 h-100">
          <PaymentForms
            :total="form.amount"
            :types="paymentTypes"
            v-model:payments="payments"
          />
          <button class="btn btn-dark w-100 mt-3" :disabled="isSubmitting" @click="submitIngreso">
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            <i class="bi bi-save me-1"></i>Registrar Ingreso
          </button>
        </div>
      </div>

    </div>
  </div>
</template>


<script setup>
import MoneyInput from '@/components/MoneyInput.vue'
import { reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import PaymentForms from '@/components/PaymentForms.vue'
import apiService from '@/services/apiService.js'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import cashboxLocalPrintService from '@/services/cashboxLocalPrintService'

//const router = useRouter()
const categories = ['Reposición en caja para Pago a Proveedores', 'Reposición por diferencia en caja', 'Devoluciones','Otros Ingresos']
const paymentTypes = ['Efectivo', 'Tarjeta', 'Cheque', 'Transferencia']

const form = reactive({
  category: '',
  amount: 0,
  reference: '',
  description: ''
})

const payments = ref([])

// FH-6b: desde un pago a proveedor sin efectivo suficiente se llega con el
// faltante (`?reposicion_proveedor=<monto>`): queda cargado como reposición en
// efectivo, y el operador sólo confirma.
const route = useRoute()
const faltanteProveedor = Number(route.query?.reposicion_proveedor || 0)
if (faltanteProveedor > 0) {
  form.category = 'Reposición en caja para Pago a Proveedores'
  form.amount = faltanteProveedor
  form.description = 'Fondos para pago a proveedor'
  payments.value = [{ type: 'Efectivo', amount: faltanteProveedor }]
}
const isSubmitting = ref(false)
const isRetryingPrint = ref(false)
const feedback = reactive({
  message: '',
  detail: '',
  variant: 'alert-success',
  canRetry: false,
  movementId: 0
})

function setFeedback({
  message = '',
  detail = '',
  variant = 'alert-success',
  canRetry = false,
  movementId = 0
} = {}) {
  feedback.message = message
  feedback.detail = detail
  feedback.variant = variant
  feedback.canRetry = canRetry
  feedback.movementId = Number(movementId || 0)
}

function resetFeedback() {
  setFeedback()
}

async function maybePrintReceipt(movementId) {
  const settings = localPrintSettingsService.getSettings()
  if (!settings.enabled) return { skipped: true }
  return cashboxLocalPrintService.printMovementReceipt({
    movementId,
    settings
  })
}

async function retryReceiptPrint() {
  if (!feedback.movementId || isRetryingPrint.value) return

  isRetryingPrint.value = true
  try {
    const printResult = await maybePrintReceipt(feedback.movementId)
    const duplicate = Boolean(printResult?.duplicate)
    setFeedback({
      message: duplicate
        ? 'El ingreso ya estaba registrado y el trabajo de impresión ya existía en el agente local.'
        : 'Ingreso registrado e impreso correctamente.',
      detail: duplicate
        ? 'No se generó un trabajo duplicado; el agente reutilizó la misma idempotencia.'
        : `Movimiento #${feedback.movementId} reimpreso correctamente.`,
      variant: 'alert-success',
      canRetry: false,
      movementId: feedback.movementId
    })
  } catch (error) {
    setFeedback({
      message: 'Ingreso registrado, pero la reimpresión del recibo volvió a fallar.',
      detail: error?.message || 'Error de impresión local.',
      variant: 'alert-warning',
      canRetry: true,
      movementId: feedback.movementId
    })
  } finally {
    isRetryingPrint.value = false
  }
}

async function submitIngreso() {
  if (isSubmitting.value) return
  resetFeedback()

  // Construir payload para el backend
  const payload = {
    concepto: form.category || 'Otros Ingresos',
    detalle: form.description || form.reference || '',
    pagos: (payments.value || []).map(p => ({
      metodo_pago: (p.type || p.metodo || '').toString().toUpperCase(),
      monto: Number(p.amount || 0),
      referencia_externa: p.reference ?? null
    }))
  }

  console.log('Payload registro ingreso varios:', payload)

  try {
    isSubmitting.value = true
    const res = await apiService.post('/api/cashbox/register-vario-income', payload)
    console.log('Respuesta register-vario-income:', res.data)
    const movementId = Number(res?.data?.movimiento_id || 0)

    let printError = null
    let printResult = null
    if (movementId > 0) {
      try {
        printResult = await maybePrintReceipt(movementId)
      } catch (error) {
        printError = error
        console.error('No se pudo imprimir el recibo del ingreso vario', error)
      }
    }

    // limpiar formulario y pagos
    form.category = ''
    form.amount = 0
    form.reference = ''
    form.description = ''
    payments.value = []

    if (printError) {
      setFeedback({
        message: 'Ingreso registrado correctamente, pero no se pudo imprimir el recibo.',
        detail: printError.message || 'Error de impresión local.',
        variant: 'alert-warning',
        canRetry: movementId > 0,
        movementId
      })
      return
    }

    const printDisabled = printResult?.skipped
    const duplicate = Boolean(printResult?.duplicate)
    setFeedback({
      message: printDisabled
        ? 'Ingreso registrado correctamente.'
        : duplicate
          ? 'Ingreso registrado correctamente. El agente local evitó duplicar la impresión.'
          : 'Ingreso registrado e impreso correctamente.',
      detail: printDisabled
        ? 'La impresión local está deshabilitada en esta estación.'
        : `Movimiento #${movementId}${duplicate ? ' ya contaba con un trabajo registrado en el agente.' : ' impreso correctamente.'}`,
      variant: 'alert-success',
      canRetry: false,
      movementId
    })
  } catch (err) {
    console.error('Error registrando ingreso varios:', err)
    window.alert('Error al registrar el ingreso. Revisa la consola para más detalles.')
  } finally {
    isSubmitting.value = false
  }
}
</script>
